import React, { useState, useEffect } from 'react';
import { Save, Database, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { DatabaseConnection, DatabaseType } from '../../types';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';

interface DatabaseConnectionFormProps {
  connection: DatabaseConnection | null;
  onClose: (newConnectionId?: string) => void;
}

const DATABASE_TYPES = [
  { value: 'POSTGRESQL', label: 'PostgreSQL', placeholder: 'postgresql://user:password@host:5432/database' },
  { value: 'MYSQL', label: 'MySQL', placeholder: 'mysql://user:password@host:3306/database' },
  { value: 'SQLSERVER', label: 'SQL Server', placeholder: 'mssql://user:password@host:1433/database' },
  { value: 'BIGQUERY', label: 'BigQuery', placeholder: 'bigquery://project-id/dataset' },
  { value: 'SNOWFLAKE', label: 'Snowflake', placeholder: 'snowflake://user:password@account/database/schema' },
  { value: 'DATABRICKS', label: 'Databricks', placeholder: 'databricks://token@host/database' },
  { value: 'CLICKHOUSE', label: 'ClickHouse', placeholder: 'clickhouse://user:password@host:8123/database' },
  { value: 'AWSATHENA', label: 'AWS Athena', placeholder: 'awsathena://aws_access_key_id:aws_secret@athena.region/database' },
  { value: 'DUCKDB', label: 'DuckDB', placeholder: 'duckdb:///path/to/database.db' },
  { value: 'SQLITE', label: 'SQLite', placeholder: 'sqlite:///path/to/database.db' },
  { value: 'REDSHIFT', label: 'Redshift', placeholder: 'redshift://user:password@host:5439/database' },
];

const DatabaseConnectionForm: React.FC<DatabaseConnectionFormProps> = ({ connection, onClose }) => {
  const [formData, setFormData] = useState<DatabaseConnection>({
    alias: '',
    connection_uri: '',
    database: '',
    schemas: [],
    use_ssh: false,
    llm_api_key: '',
    metadata: {},
  });
  const [selectedType, setSelectedType] = useState('POSTGRESQL');
  const [schemaInput, setSchemaInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (connection) {
      setFormData(connection);
      setSchemaInput(connection.schemas?.join(', ') || '');
      // Try to detect database type from URI
      const uri = connection.connection_uri || '';
      const type = DATABASE_TYPES.find(t => uri.toLowerCase().includes(t.value.toLowerCase()));
      if (type) setSelectedType(type.value);
    }
  }, [connection]);

  const handleInputChange = (field: keyof DatabaseConnection) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSchemaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSchemaInput(e.target.value);
    const schemas = e.target.value
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);
    setFormData(prev => ({
      ...prev,
      schemas,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.alias || !formData.connection_uri) {
      toast.error('Please provide both alias and connection URI');
      return;
    }

    // Clean up the connection URI - remove channel_binding and any extra parameters after sslmode
    let cleanedUri = formData.connection_uri || '';
    
    // Remove channel_binding parameter
    cleanedUri = cleanedUri.replace(/&channel_binding=[^&]*/g, '');
    
    // If there are extra parameters after sslmode, ensure proper formatting
    if (cleanedUri.includes('sslmode=require')) {
      // Split at sslmode and reconstruct properly
      const parts = cleanedUri.split('sslmode=require');
      if (parts.length > 1 && parts[1]) {
        // Remove any malformed query params after sslmode
        cleanedUri = parts[0] + 'sslmode=require';
      }
    }
    
    const cleanedFormData = {
      ...formData,
      connection_uri: cleanedUri
    };

    setSaving(true);
    try {
      if (connection?.id) {
        await api.updateDatabaseConnection(connection.id, cleanedFormData);
        toast.success('Database connection updated successfully');
        onClose();
      } else {
        const newConnection = await api.createDatabaseConnection(cleanedFormData);
        toast.success('Database connection created successfully');
        onClose(newConnection.id);
      }
    } catch (error) {
      toast.error('Failed to save database connection');
    } finally {
      setSaving(false);
    }
  };

  const currentPlaceholder = DATABASE_TYPES.find(t => t.value === selectedType)?.placeholder || '';

  return (
    <Dialog open={true} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            {connection ? 'Edit Database Connection' : 'Add Database Connection'}
          </DialogTitle>
          <DialogDescription>
            Configure your database connection settings
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="alias">
              Connection Alias <span className="text-destructive">*</span>
            </Label>
            <Input
              id="alias"
              type="text"
              value={formData.alias}
              onChange={handleInputChange('alias')}
              placeholder="My Database"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">Database Type</Label>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger id="type">
                <SelectValue placeholder="Select database type" />
              </SelectTrigger>
              <SelectContent>
                {DATABASE_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="uri">
              Connection URI <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="uri"
              value={formData.connection_uri}
              onChange={handleInputChange('connection_uri')}
              placeholder={currentPlaceholder}
              rows={2}
              className="font-mono text-sm"
              required
            />
            <p className="text-xs text-muted-foreground">
              Enter the full connection string including credentials
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="database">Database Name</Label>
            <Input
              id="database"
              type="text"
              value={formData.database || ''}
              onChange={handleInputChange('database')}
              placeholder="Optional: specific database to connect to"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="schemas">Schemas</Label>
            <Input
              id="schemas"
              type="text"
              value={schemaInput}
              onChange={handleSchemaChange}
              placeholder="public, analytics (comma-separated)"
            />
            <p className="text-xs text-muted-foreground">
              Comma-separated list of schemas to include (leave empty for all)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="api-key">LLM API Key</Label>
            <Input
              id="api-key"
              type="password"
              value={formData.llm_api_key || ''}
              onChange={handleInputChange('llm_api_key')}
              placeholder="Optional: Override default OpenAI API key"
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to use the default API key from settings
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="use_ssh"
              checked={formData.use_ssh || false}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, use_ssh: checked as boolean }))}
            />
            <Label htmlFor="use_ssh" className="text-sm font-normal">
              Use SSH Tunnel
            </Label>
          </div>

          {formData.use_ssh && (
            <Alert>
              <AlertDescription>
                <p className="font-medium mb-1">SSH Settings</p>
                <p className="text-xs">
                  Note: SSH configuration requires additional setup in the engine service
                </p>
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose()}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {connection ? 'Update' : 'Create'} Connection
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DatabaseConnectionForm;